import { signInAnonymously, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

// Authentication Service
export const authService = {
  // Sign up new user with email and password
  async signUp(email, password, userData = {}) {
    try {
      console.log('🔐 Creating new user account...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('✅ User account created:', user.uid);
      
      // Create user profile
      const profileData = {
        id: user.uid,
        email: user.email,
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        profile_complete: false,
        role: 'client' // Default role
      };
      
      await this.createUserProfile(user.uid, profileData);
      console.log('✅ User profile created');
      
      return { success: true, user, profile: profileData };
    } catch (error) {
      console.error('❌ Sign up error:', error);
      return { success: false, error: error.message };
    }
  },

  // Sign in with email and password
  async signIn(email, password) {
    try {
      console.log('🔐 Signing in user...');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('✅ User signed in:', user.uid);
      
      // Get or create user profile
      const profileResult = await this.getUserProfile(user.uid);
      
      return { success: true, user, profile: profileResult.data };
    } catch (error) {
      console.error('❌ Sign in error:', error);
      return { success: false, error: error.message };
    }
  },

  // Sign out user
  async signOut() {
    try {
      await auth.signOut();
      console.log('✅ User signed out');
      return { success: true };
    } catch (error) {
      console.error('❌ Sign out error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get current authenticated user
  async getCurrentUser() {
    try {
      const user = auth.currentUser;
      
      if (!user) {
        console.log('❌ No user currently authenticated');
        return null;
      }
      
      console.log('✅ Current user:', user.uid);
      return user;
    } catch (error) {
      console.error('❌ Error getting current user:', error);
      return null;
    }
  },

  // Create user profile
  async createUserProfile(userId, profileData) {
    try {
      // Filter out undefined values to prevent Firestore errors
      const cleanProfileData = {};
      Object.keys(profileData).forEach(key => {
        if (profileData[key] !== undefined) {
          cleanProfileData[key] = profileData[key];
        }
      });
      
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, cleanProfileData);
      console.log('✅ User profile created for:', userId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error creating user profile:', error);
      return { success: false, error: error.message };
    }
  },

  // Get or create user profile
  async getUserProfile(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return { success: true, data: { id: userDoc.id, ...userDoc.data() } };
      } else {
        // Create default user profile if it doesn't exist
        const defaultProfile = {
          id: userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          profile_complete: false,
          role: 'client'
        };
        
        await setDoc(userRef, defaultProfile);
        return { success: true, data: defaultProfile };
      }
    } catch (error) {
      console.error('Error getting user profile:', error);
      return { success: false, error: error.message };
    }
  },

  // Update user profile
  async updateUserProfile(userId, updates) {
    try {
      // Filter out undefined values to prevent Firestore errors
      const cleanUpdates = {};
      Object.keys(updates).forEach(key => {
        if (updates[key] !== undefined) {
          cleanUpdates[key] = updates[key];
        }
      });
      
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        ...cleanUpdates,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      console.log('✅ User profile updated for:', userId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating user profile:', error);
      return { success: false, error: error.message };
    }
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!auth.currentUser;
  },

  // Get authentication state
  getAuthState() {
    return auth.currentUser;
  }
};
